/**
 * «Завести культуру в план» — вход в структуру посевов планового сезона.
 *
 * История (founder UAT 19.08): экран назначения техкарт показывал строки плана
 * и покрытие, но завести саму строку было нечем — экрана распределения культур
 * по полям ещё нет, и вариант плана оставался вечно пустым. Классика «расчёт
 * есть — вводить нечем».
 *
 * Почему выбор ПОЛЕЙ, а не «культура на 400 га». Строка плана в системе не
 * хранится отдельной записью: она собирается из посевов варианта, площадь берёт
 * с контура поля, предшественника — с факта прошлого сезона. Поле в форме
 * «культура + гектары» дало бы вторую, ручную площадь рядом с земельным
 * банком — ровно ту развилку, из-за которой соседние экраны когда-то показывали
 * разную пашню.
 *
 * Занятые поля из списка не прячем: агроном должен видеть, что поле уже под
 * другой культурой этого варианта, — иначе «моего поля нет в списке» читается
 * как поломка.
 */
import { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { useCan } from '@/lib/auth/usePermissions';
import { useCropPicker, type CropPickerItem } from '@/components/fields/card/api';
import {
  sortFieldsForPicker,
  totalAreaOf,
  useAddCropToVariant,
  useVariantFields,
  type VariantField,
} from '@/lib/planning/planCrops';

const HA = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 });

/** Поиск без регистра и без разницы «ё/е» — агроном пишет как привык. */
export function matchesQuery(name: string, query: string): boolean {
  const norm = (s: string) => s.toLowerCase().replace(/ё/g, 'е').trim();
  return norm(name).includes(norm(query));
}

/** Культуры номенклатуры хозяйства, у которых есть системная культура. */
export function pickableCrops(items: readonly CropPickerItem[]): { id: string; name: string }[] {
  const seen = new Set<string>();
  const out: { id: string; name: string }[] = [];
  for (const item of items) {
    // Без системной культуры строку плана не с чем связать: и техкарта, и цена
    // реализации, и предшественник живут на `reference.crops`.
    if (!item.crop_id || seen.has(item.crop_id)) continue;
    seen.add(item.crop_id);
    out.push({ id: item.crop_id, name: item.name || item.canonical_name || 'культура без названия' });
  }
  return out.sort((a, b) => a.name.localeCompare(b.name, 'ru'));
}

export function AddCropDialog({
  scenarioId,
  variantName,
  seasonYear,
  open,
  onClose,
}: {
  scenarioId: string;
  variantName: string;
  seasonYear: number;
  open: boolean;
  onClose: () => void;
}) {
  const crops = useCropPicker();
  const fields = useVariantFields(scenarioId, open);
  const add = useAddCropToVariant();

  const [cropId, setCropId] = useState<string | null>(null);
  const [cropQuery, setCropQuery] = useState('');
  const [fieldQuery, setFieldQuery] = useState('');
  const [yieldCHa, setYieldCHa] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const cropOptions = useMemo(() => pickableCrops(crops.data ?? []), [crops.data]);
  const shownCrops = cropOptions.filter((c) => matchesQuery(c.name, cropQuery));
  const cropName = cropOptions.find((c) => c.id === cropId)?.name ?? '';

  const allFields = useMemo(() => sortFieldsForPicker(fields.data ?? []), [fields.data]);
  const shownFields = allFields.filter((f) => matchesQuery(f.name, fieldQuery));
  const freeFields = allFields.filter((f) => !f.occupied_by && !f.occupied_by_subzones);
  const selectedArea = totalAreaOf(allFields, selected);

  const reset = () => {
    setCropId(null);
    setCropQuery('');
    setFieldQuery('');
    setYieldCHa('');
    setSelected(new Set());
  };

  const close = () => {
    reset();
    onClose();
  };

  const toggle = (field: VariantField) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(field.id)) next.delete(field.id);
      else next.add(field.id);
      return next;
    });
  };

  const submit = () => {
    if (!cropId || selected.size === 0 || add.isPending) return;
    const parsed = yieldCHa.trim() === '' ? null : Number(yieldCHa.replace(',', '.'));
    add.mutate(
      {
        scenarioId,
        cropId,
        cropName,
        fieldIds: [...selected],
        plannedYieldCHa: parsed !== null && Number.isFinite(parsed) && parsed > 0 ? parsed : null,
      },
      { onSuccess: () => close() },
    );
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Завести культуру в план</DialogTitle>
          <DialogDescription>
            Вариант «{variantName}», сезон {seasonYear}. Выберите культуру и поля, на которых
            она будет расти. Площадь считается по контурам полей, предшественник — по факту
            прошлого сезона; вводить их рукой не нужно.
          </DialogDescription>
        </DialogHeader>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <Label htmlFor="add-crop-search">Культура из номенклатуры хозяйства</Label>
              <Input
                id="add-crop-search"
                value={cropQuery}
                onChange={(e) => setCropQuery(e.target.value)}
                placeholder="Поиск по названию"
                autoFocus
              />
              {crops.isLoading && <Skeleton className="h-24 w-full" />}
              {!crops.isLoading && cropOptions.length === 0 && (
                <p className="text-sm text-muted-foreground">
                  В номенклатуре хозяйства нет ни одной культуры, связанной с общим
                  справочником. Заведите культуру в номенклатуру — тогда её можно будет
                  посеять в плане.
                </p>
              )}
              {cropOptions.length > 0 && (
                <ScrollArea className="max-h-40 rounded-md border border-border">
                  <div className="flex flex-col p-1">
                    {shownCrops.map((crop) => (
                      <button
                        key={crop.id}
                        type="button"
                        aria-pressed={crop.id === cropId}
                        onClick={() => setCropId(crop.id)}
                        className={
                          crop.id === cropId
                            ? 'rounded-md bg-primary/10 px-3 py-2 text-left text-sm font-medium text-primary'
                            : 'rounded-md px-3 py-2 text-left text-sm text-foreground hover:bg-accent'
                        }
                      >
                        {crop.name}
                      </button>
                    ))}
                    {shownCrops.length === 0 && (
                      <p className="px-3 py-2 text-sm text-muted-foreground">
                        По запросу ничего не нашлось.
                      </p>
                    )}
                  </div>
                </ScrollArea>
              )}
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="add-crop-yield">Плановая урожайность, ц/га (необязательно)</Label>
              <Input
                id="add-crop-yield"
                inputMode="decimal"
                value={yieldCHa}
                onChange={(e) => setYieldCHa(e.target.value)}
                placeholder="например, 55"
              />
              <p className="text-xs text-muted-foreground">
                Без урожайности валовка и выручка по этой культуре не посчитаются — их
                поставят прочерком, а не нулём.
              </p>
            </div>

            <div className="flex flex-col gap-2">
              <div className="flex flex-wrap items-center gap-2">
                <Label htmlFor="add-crop-fields">Поля</Label>
                <span className="text-xs text-muted-foreground">
                  свободно в варианте: {freeFields.length}
                </span>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="ml-auto"
                  disabled={freeFields.length === 0}
                  onClick={() => setSelected(new Set(freeFields.map((f) => f.id)))}
                >
                  Выбрать все свободные
                </Button>
                {selected.size > 0 && (
                  <Button type="button" size="sm" variant="ghost" onClick={() => setSelected(new Set())}>
                    Снять выбор
                  </Button>
                )}
              </div>
              <Input
                id="add-crop-fields"
                value={fieldQuery}
                onChange={(e) => setFieldQuery(e.target.value)}
                placeholder="Поиск поля по названию"
              />
              {fields.isLoading && <Skeleton className="h-40 w-full" />}
              {!fields.isLoading && allFields.length === 0 && (
                <p className="text-sm text-muted-foreground">
                  У хозяйства нет полей, которые считаются пашней. Проверьте угодье в
                  карточках полей — структура и доли считаются по нему.
                </p>
              )}
              {allFields.length > 0 && (
                <ScrollArea className="max-h-64 rounded-md border border-border">
                  <div className="flex flex-col p-1">
                    {shownFields.map((field) => {
                      const busy = !!field.occupied_by || field.occupied_by_subzones;
                      return (
                        <label
                          key={field.id}
                          className={
                            busy
                              ? 'flex min-h-10 items-center gap-3 rounded-md px-3 py-2 text-sm text-muted-foreground'
                              : 'flex min-h-10 cursor-pointer items-center gap-3 rounded-md px-3 py-2 text-sm hover:bg-accent'
                          }
                        >
                          <Checkbox
                            checked={selected.has(field.id)}
                            disabled={busy}
                            onCheckedChange={() => toggle(field)}
                            aria-label={`Поле ${field.name}`}
                          />
                          <span className="text-foreground">{field.name}</span>
                          <span className="tabular-nums text-muted-foreground">
                            {HA.format(Math.round(field.area_ha))} га
                          </span>
                          <span className="ml-auto text-xs text-muted-foreground">
                            {field.occupied_by_subzones
                              ? 'в варианте засеяны зоны поля'
                              : field.occupied_by
                                ? `занято: ${field.occupied_by}`
                                : field.predecessor_name
                                  ? `предшественник: ${field.predecessor_name.toLowerCase()}`
                                  : 'предшественник неизвестен'}
                          </span>
                        </label>
                      );
                    })}
                    {shownFields.length === 0 && (
                      <p className="px-3 py-2 text-sm text-muted-foreground">
                        По запросу ничего не нашлось.
                      </p>
                    )}
                  </div>
                </ScrollArea>
              )}
              <p className="text-sm text-foreground">
                Выбрано полей: {selected.size} · {HA.format(Math.round(selectedArea))} га
              </p>
            </div>
          </div>

          <DialogFooter className="mt-4">
            <Button type="button" variant="outline" onClick={close}>
              Отмена
            </Button>
            <Button type="submit" disabled={!cropId || selected.size === 0 || add.isPending}>
              {add.isPending ? 'Заводим…' : 'Завести в план'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** Кнопка + диалог одним куском: экранам не нужно держать состояние открытия. */
export function AddCropButton({
  scenarioId,
  variantName,
  seasonYear,
  emptyState = false,
}: {
  scenarioId: string;
  variantName: string;
  seasonYear: number;
  emptyState?: boolean;
}) {
  const canSow = useCan('sowings', 'c');
  const [open, setOpen] = useState(false);

  if (!canSow) {
    return emptyState ? (
      <p className="text-sm text-muted-foreground">
        Заводить культуры в план может пользователь с правом на посевы — обратитесь к
        агроному или администратору.
      </p>
    ) : null;
  }

  return (
    <>
      <Button
        type="button"
        size="sm"
        variant={emptyState ? 'default' : 'outline'}
        onClick={() => setOpen(true)}
      >
        {emptyState ? 'Завести культуру в план' : '+ Культура в план'}
      </Button>
      {open && (
        <AddCropDialog
          scenarioId={scenarioId}
          variantName={variantName}
          seasonYear={seasonYear}
          open={open}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}
